import { useState, useEffect, useCallback } from 'react'
import { gql } from 'graphql-tag'
import Server from '../server/VMSProServerAdapter'
import { DecisionFolderLite, DecisionLite, DecisionNodeLite } from '../types/decisionTypes'

export type UseDecisionFolder2Params = {
  decisionFolderId: string
  accountId: string
}

export type UseDecisionFolder2Result = {
  ancestors: DecisionFolderLite[]
  folder: DecisionFolderLite
  children: DecisionNodeLite[]
}

export type UseDecisionFolder2Hook = {
  data: UseDecisionFolder2Result | null
  loading: boolean
  refetch: () => void
}

const DECISION_NODE2_QUERY = gql`
  query DecisionNode2($accountId: ID!, $decisionNodeId: ID!, $ancestors: Boolean!, $children: Boolean!) {
    decisionNode2(
      accountId: $accountId
      decisionNodeId: $decisionNodeId
      ancestors: $ancestors
      children: $children
    ) {
      decisionNode {
        id
        name
        entityType
        description {
          value
        }
        optionsCount
        policies {
          userId
          policyKey
        }
        decisionType
      }
      ancestors {
        id
        name
        entityType
        description {
          value
        }
        optionsCount
        policies {
          userId
          policyKey
        }
        decisionType
      }
      children {
        id
        name
        entityType
        description {
          value
        }
        optionsCount
        policies {
          userId
          policyKey
        }
        decisionType
      }
    }
  }
`

function transformToDecisionFolderLite(item: any): DecisionFolderLite {
  if (item.entityType !== 'DecisionFolder') {
    throw new Error(`Expected DecisionFolder, got ${item.entityType}`)
  }

  return {
    id: item.id,
    entityType: 'DecisionFolder',
    name: item.name,
    description: item.description,
    optionsCount: item.optionsCount,
    policies: item.policies,
    decisionType: null, // Always null for folders
  }
}

function transformToDecisionLite(item: any): DecisionLite {
  if (item.entityType !== 'Decision') {
    throw new Error(`Expected Decision, got ${item.entityType}`)
  }

  return {
    id: item.id,
    entityType: 'Decision',
    name: item.name,
    optionsCount: item.optionsCount,
    description: item.description,
    decisionType: item.decisionType,
  }
}

function transformToDecisionNodeLite(item: any): DecisionNodeLite {
  if (item.entityType === 'DecisionFolder') {
    return transformToDecisionFolderLite(item)
  } else if (item.entityType === 'Decision') {
    return transformToDecisionLite(item)
  }
  throw new Error(`Unknown entity type: ${item.entityType}`)
}

export function useDecisionFolder2({
  decisionFolderId,
  accountId,
}: UseDecisionFolder2Params): UseDecisionFolder2Hook {
  const [data, setData] = useState<UseDecisionFolder2Result | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)

  // Refetch function to trigger a refresh
  const refetch = useCallback(() => {
    setRefreshKey(prev => prev + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      setLoading(true)

      try {
        const response = await Server.graphql({
          query: DECISION_NODE2_QUERY,
          variables: {
            accountId,
            decisionNodeId: decisionFolderId,
            ancestors: true,
            children: true,
          },
        })

        if (cancelled) return

        const { decisionNode, ancestors, children } = response.data.decisionNode2

        // Ensure the main node is a folder
        const folder = transformToDecisionFolderLite(decisionNode)

        // Transform ancestors (should all be folders)
        const transformedAncestors = ancestors.map(transformToDecisionFolderLite)

        // Transform children (can be folders or decisions)
        const transformedChildren = children.map(transformToDecisionNodeLite)

        const result: UseDecisionFolder2Result = {
          ancestors: transformedAncestors,
          folder,
          children: transformedChildren,
        }

        if (!cancelled) {
          setData(result)
        }
      } catch (error) {
        console.error('Error fetching folder data:', error)
        if (!cancelled) {
          setData(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchData()

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      cancelled = true
    }
  }, [decisionFolderId, accountId, refreshKey])

  return {
    data,
    loading,
    refetch,
  }
}
